import React from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Link as MuiLink,
  IconButton,
  Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { openCookieSettings } from '../utils/cookieConsent';
import { partners } from '../constants/partners';

const quickLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Market', to: '/market' },
  { label: 'Blog', to: '/blog' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Contact', to: '/contact' },
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms of Service', to: '/terms-of-service' },
];

const platformLinks = [
  { label: 'Chat Assistant', to: '/chat' },
  { label: 'Schedule Consultation', to: '/schedule' },
  { label: 'Email Support', href: 'mailto:support@keyta.ca' },
];

const socialLinks = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/keyta-ca',
    icon: <LinkedInIcon fontSize="small" />,
  },
  // {
  //   label: 'Instagram',
  //   href: 'https://www.instagram.com',
  //   icon: <InstagramIcon fontSize="small" />,
  // },
  // {
  //   label: 'Facebook',
  //   href: 'https://www.facebook.com',
  //   icon: <FacebookIcon fontSize="small" />,
  // },
  // {
  //   label: 'YouTube',
  //   href: 'https://www.youtube.com',
  //   icon: <YouTubeIcon fontSize="small" />,
  // },
];

const footerLinkSx = (theme) => ({
  color: theme.modern.colors.modern.slate[600],
  textDecoration: 'none',
  fontSize: '0.95rem',
  fontWeight: 500,
  transition: 'color 0.2s ease',
  '&:hover': {
    color: theme.modern.colors.modern.mainColor[700],
  },
});

const getPartnerInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const SiteFooter = () => {
  const theme = useTheme();
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        zIndex: 7,
        mt: 'auto',
        borderTop: `1px solid ${alpha(theme.modern.colors.modern.blue[300], 0.55)}`,
        background: `linear-gradient(180deg, ${alpha('#ffffff', 0.68)} 0%, ${alpha(theme.modern.colors.modern.blue[100], 0.84)} 100%)`,
        backdropFilter: 'blur(8px)',
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 4, md: 5 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 5 }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'flex-start' }}
        >
          <Box sx={{ maxWidth: 340 }}>
            <Stack direction="column" spacing={1.1} alignItems="flex-start" sx={{ mb: 0 }}>
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: 80, md: 100 },
                  height: { xs: 80, md: 100 },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: -1,
                    // borderRadius: '50%',
                    // background: `linear-gradient(135deg, ${alpha(theme.modern.colors.modern.mainColor[500], 0.22)} 0%, ${alpha(theme.modern.colors.modern.blue[500], 0.24)} 100%)`,
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    // borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'grid',
                    placeItems: 'center',
                    // backgroundColor: alpha('#ffffff', 0.94),
                    // boxShadow: `0 10px 24px ${alpha(theme.modern.colors.modern.mainColor[700], 0.42)}`,
                  }}
                >
                  <Box
                    component="img"
                    src="/logo-complete.png?v=20260611"
                    alt="KeyTa logo"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      padding: '0px',
                      boxSizing: 'border-box',
                      display: 'block',
                    }}
                  />
                </Box>
              </Box>
              {/* <Box>
                <Typography
                  variant="h4"
                  sx={{
                    lineHeight: 1,
                    fontWeight: 800,
                    color: theme.modern.colors.modern.slate[900],
                    letterSpacing: '-0.02em',
                  }}
                >
                  KeyTa
                </Typography>
              </Box> */}
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: theme.modern.colors.modern.slate[600],
                lineHeight: 1.75,
              }}
            >
              AI-powered real estate guidance for homeowners, from pricing and planning to listing and closing.
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  aria-label={social.label}
                  component="a"
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  size="small"
                  sx={{
                    color: theme.modern.colors.modern.slate[600],
                    border: `1px solid ${alpha(theme.modern.colors.modern.blue[300], 0.6)}`,
                    backgroundColor: alpha('#ffffff', 0.66),
                    '&:hover': {
                      color: theme.modern.colors.modern.mainColor[700],
                      borderColor: alpha(theme.modern.colors.modern.mainColor[500], 0.8),
                      backgroundColor: alpha('#ffffff', 0.95),
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Stack>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 6, md: 8 }}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.4,
                  color: theme.modern.colors.modern.slate[900],
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Explore
              </Typography>
              <Stack spacing={1}>
                {quickLinks.map((link) => (
                  <MuiLink
                    key={link.label}
                    component={RouterLink}
                    to={link.to}
                    sx={footerLinkSx(theme)}
                  >
                    {link.label}
                  </MuiLink>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.4,
                  color: theme.modern.colors.modern.slate[900],
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Platform
              </Typography>
              <Stack spacing={1}>
                {platformLinks.map((link) =>
                  link.to ? (
                    <MuiLink
                      key={link.label}
                      component={RouterLink}
                      to={link.to}
                      sx={footerLinkSx(theme)}
                    >
                      {link.label}
                    </MuiLink>
                  ) : (
                    <MuiLink
                      key={link.label}
                      href={link.href}
                      sx={footerLinkSx(theme)}
                    >
                      {link.label}
                    </MuiLink>
                  )
                )}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={openCookieSettings}
                  sx={{
                    ...footerLinkSx(theme),
                    border: 0,
                    background: 'none',
                    padding: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Cookie Settings
                </MuiLink>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1.4,
                  color: theme.modern.colors.modern.slate[900],
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}
              >
                Partners
              </Typography>
              <Stack direction="column" spacing={1} alignItems="flex-start">
                {partners.slice(0, 3).map((partner) => (
                  <Stack
                    key={partner.name}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    component={partner.url ? 'a' : 'div'}
                    href={partner.url || undefined}
                    target={partner.url ? '_blank' : undefined}
                    rel={partner.url ? 'noreferrer' : undefined}
                    sx={{
                      width: 'fit-content',
                      textDecoration: 'none',
                      cursor: partner.url ? 'pointer' : 'default',
                      '&:hover .partner-logo-box': partner.url
                        ? {
                            borderColor: alpha(theme.modern.colors.modern.mainColor[500], 0.85),
                            backgroundColor: alpha('#ffffff', 0.95),
                          }
                        : undefined,
                    }}
                    aria-label={partner.url ? partner.name : undefined}
                    title={partner.name}
                  >
                    <Box
                      className="partner-logo-box"
                      sx={{
                        // width: PARTNER_LOGO_SIZE,
                        // height: PARTNER_LOGO_SIZE,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        display: 'grid',
                        placeItems: 'center',
                        border: `1px solid ${alpha(theme.modern.colors.modern.blue[300], 0.55)}`,
                        backgroundColor: alpha('#ffffff', 0.84),
                        flexShrink: 0,
                      }}
                    >
                      {partner.logo ? (
                        <Box
                          component="img"
                          src={partner.logo}
                          alt={partner.logoAlt || `${partner.name} logo`}
                          sx={{
                            width: '220px',
                            height: '55px',
                            objectFit: 'contain',
                            p: '4px',
                            boxSizing: 'border-box',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.modern.colors.modern.mainColor[700],
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            lineHeight: 1,
                          }}
                        >
                          {getPartnerInitials(partner.name)}
                        </Typography>
                      )}
                    </Box>

                    {!partner.logo && (
                      <Typography className="partner-name" sx={footerLinkSx(theme)}>
                        {partner.name}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2.5, borderColor: alpha(theme.modern.colors.modern.blue[300], 0.45) }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="caption" sx={{ color: theme.modern.colors.modern.slate[600] }}>
            {`Copyright ${year} KeyTa. All rights reserved.`}
          </Typography>

          <Typography variant="caption" sx={{ color: theme.modern.colors.modern.slate[500] }}>
            Built for modern Canadian real estate teams.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default SiteFooter;